import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import api from '@/lib/api.ts'
import { Plus, CheckCircle2, Printer, Package, Mail, PenTool, ChevronRight } from 'lucide-react'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  currencyColumn,
  dateColumn,
  textColumn,
  statusColumn,
  useToast,
} from '@/components/ui'
import type { ColumnDef } from '@/components/ui'

interface LxpJob {
  id: string
  status: 'new' | 'processing' | 'sent' | 'error'
  price?: number
  pages?: number
  createdAt: string
}

const lxpColumns: ColumnDef<LxpJob>[] = [
  {
    ...textColumn<LxpJob>('id', 'Job-ID'),
    cell: ({ row }) => (
      <span className="font-mono text-caption text-gray-600">#{row.original.id}</span>
    ),
  },
  dateColumn<LxpJob>('createdAt', 'Datum'),
  {
    ...textColumn<LxpJob>('pages', 'Seiten'),
    cell: ({ row }) => row.original.pages ?? '—',
  },
  {
    ...currencyColumn<LxpJob>('price', 'Preis'),
  },
  statusColumn<LxpJob>('status'),
]

export function ShippingPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'dhl' | 'lxp'>('overview')
  const [showDhlForm, setShowDhlForm] = useState(false)
  const [dhlResult, setDhlResult] = useState<{ shipmentTrackingNumber: string; label: string } | null>(null)
  const { toast } = useToast()

  const lxpJobs = useQuery<LxpJob[]>({
    queryKey: ['lxp-jobs'],
    queryFn: () => api.get('/shipping/lxp/jobs').then((r) => r.data),
    enabled: activeTab === 'lxp',
    retry: false,
  })

  const lxpBalance = useQuery<{ balance: number }>({
    queryKey: ['lxp-balance'],
    queryFn: () => api.get('/shipping/lxp/balance').then((r) => r.data),
    enabled: activeTab === 'lxp',
    retry: false,
  })

  const createShipment = useMutation({
    mutationFn: (data: unknown) => api.post('/shipping/dhl/shipments', data).then((r) => r.data),
    onSuccess: (data) => {
      setDhlResult(data)
      toast({ title: 'Paketschein erstellt', tone: 'success' })
    },
    onError: () => {
      toast({ title: 'Fehler beim Erstellen des Paketscheins', tone: 'danger' })
    },
  })

  const [form, setForm] = useState({
    consigneeName: '', street: '', housenumber: '', zip: '', city: '', weight: '1',
  })

  const handleDhlSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createShipment.mutate({
      product: 'V01PAK',
      shipper: { name1: 'implec GmbH', streetAddress: 'Musterstr. 1', postalCode: '38312', city: 'Hedeper', country: 'DEU' },
      consignee: { name1: form.consigneeName, streetAddress: form.street, housenumber: form.housenumber, postalCode: form.zip, city: form.city, country: 'DEU' },
      parcel: { weight: parseFloat(form.weight) },
    })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Versand"
        description="DHL Paketversand, Briefpost, handgeschriebene Briefe"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/versand/paketschein" className="group">
          <Card className="p-5 transition-all hover:border-brand-300 hover:shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h3 className="text-body font-semibold text-gray-800">DHL Paketschein</h3>
                  <p className="text-caption text-gray-500">Paket erstellen & Label drucken</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-brand-600 transition-colors" />
            </div>
          </Card>
        </Link>

        <Link to="/versand/briefpost" className="group">
          <Card className="p-5 transition-all hover:border-brand-300 hover:shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <h3 className="text-body font-semibold text-gray-800">E-POSTBUSINESS</h3>
                  <p className="text-caption text-gray-500">Digitale Briefpost versenden</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-brand-600 transition-colors" />
            </div>
          </Card>
        </Link>

        <Link to="/versand/pensaki" className="group">
          <Card className="p-5 transition-all hover:border-brand-300 hover:shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <PenTool className="w-5 h-5 text-purple-700" />
                </div>
                <div>
                  <h3 className="text-body font-semibold text-gray-800">PENSAKI</h3>
                  <p className="text-caption text-gray-500">Einzelbriefe in echter Handschrift</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-brand-600 transition-colors" />
            </div>
          </Card>
        </Link>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value="overview">Uebersicht</TabsTrigger>
          <TabsTrigger value="dhl">DHL Schnellversand</TabsTrigger>
          <TabsTrigger value="lxp">LetterXpress Brief</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <Card.Body>
              <p className="text-small text-gray-600">
                Waehlen Sie oben einen Versandkanal aus oder nutzen Sie die Schnellzugriff-Karten
                fuer die detaillierten Versand-Seiten.
              </p>
            </Card.Body>
          </Card>
        </TabsContent>

        <TabsContent value="dhl">
          <div className="space-y-4">
            <Button
              variant="primary"
              leadingIcon={<Plus className="w-4 h-4" />}
              onClick={() => setShowDhlForm(!showDhlForm)}
            >
              Paketschein erstellen
            </Button>

            {showDhlForm && (
              <form onSubmit={handleDhlSubmit}>
                <Card>
                  <Card.Header>
                    <Card.Title>Empfaenger</Card.Title>
                  </Card.Header>
                  <Card.Body className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Input
                          label="Name/Firma"
                          required
                          value={form.consigneeName}
                          onChange={(e) => setForm((f) => ({ ...f, consigneeName: e.target.value }))}
                        />
                      </div>
                      <Input
                        label="Strasse"
                        required
                        value={form.street}
                        onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))}
                      />
                      <Input
                        label="Hausnummer"
                        required
                        value={form.housenumber}
                        onChange={(e) => setForm((f) => ({ ...f, housenumber: e.target.value }))}
                      />
                      <Input
                        label="PLZ"
                        required
                        value={form.zip}
                        onChange={(e) => setForm((f) => ({ ...f, zip: e.target.value }))}
                      />
                      <Input
                        label="Stadt"
                        required
                        value={form.city}
                        onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                      />
                      <Input
                        label="Gewicht (kg)"
                        type="number"
                        step="0.1"
                        min="0.1"
                        required
                        value={form.weight}
                        onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value }))}
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        type="submit"
                        variant="primary"
                        loading={createShipment.isPending}
                        disabled={createShipment.isPending}
                      >
                        {createShipment.isPending ? 'Wird erstellt…' : 'Paketschein erstellen'}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setShowDhlForm(false)}
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </form>
            )}

            {dhlResult && (
              <Card className="border-green-200 bg-green-50">
                <Card.Body className="space-y-2">
                  <div className="flex items-center gap-2 text-green-800 font-semibold">
                    <CheckCircle2 className="w-5 h-5" />
                    Paketschein erstellt!
                  </div>
                  <p className="text-small text-green-700">
                    Sendungsnummer: <span className="font-mono font-bold">{dhlResult.shipmentTrackingNumber}</span>
                  </p>
                  {dhlResult.label && (
                    <Button
                      variant="link"
                      size="sm"
                      leadingIcon={<Printer className="w-4 h-4" />}
                      onClick={() => {
                        const link = document.createElement('a')
                        link.href = `data:application/pdf;base64,${dhlResult.label}`
                        link.download = `DHL_${dhlResult.shipmentTrackingNumber}.pdf`
                        link.click()
                      }}
                    >
                      Label herunterladen (PDF)
                    </Button>
                  )}
                </Card.Body>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="lxp">
          <div className="space-y-4">
            {lxpBalance.data && (
              <Card>
                <Card.Body className="flex items-center justify-between py-3">
                  <span className="text-small text-gray-600">Guthaben</span>
                  <span className="font-bold text-gray-900">
                    {lxpBalance.data.balance.toLocaleString('de', { minimumFractionDigits: 2 })} EUR
                  </span>
                </Card.Body>
              </Card>
            )}

            <Card>
              <Card.Header>
                <Card.Title>Letzte Briefauftraege</Card.Title>
              </Card.Header>
              {lxpJobs.isLoading ? (
                <LoadingState />
              ) : lxpJobs.isError ? (
                <ErrorState
                  title="LetterXpress nicht erreichbar"
                  description="Nicht konfiguriert oder Verbindungsfehler."
                  details={(lxpJobs.error as Error)?.message}
                />
              ) : !lxpJobs.data?.length ? (
                <EmptyState title="Keine Auftraege gefunden." />
              ) : (
                <DataTable<LxpJob>
                  data={lxpJobs.data}
                  columns={lxpColumns}
                  getRowId={(row) => row.id}
                  className="rounded-none border-0 shadow-none"
                />
              )}
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
